import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { RosterProfile } from '@/lib/equip-extras'
import type { EmploymentStatus } from '@/data/types'

interface ImpersonationContextValue {
  selectedPersona: RosterProfile | null
  setSelectedPersona: (profile: RosterProfile | null) => void
  clearPersona: () => void
  employmentStatusOverride: EmploymentStatus | null
  setEmploymentStatusOverride: (status: EmploymentStatus | null) => void
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null)

interface ImpersonationProviderProps {
  children: ReactNode
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const [selectedPersona, setSelectedPersonaRaw] = useState<RosterProfile | null>(null)
  const [employmentStatusOverride, setEmploymentStatusOverride] = useState<EmploymentStatus | null>(null)

  const setSelectedPersona = useCallback((profile: RosterProfile | null) => {
    setSelectedPersonaRaw(profile)
    setEmploymentStatusOverride(profile?.employmentStatus ?? null)
  }, [])

  const clearPersona = useCallback(() => {
    setSelectedPersonaRaw(null)
    setEmploymentStatusOverride(null)
  }, [])

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      selectedPersona,
      setSelectedPersona,
      clearPersona,
      employmentStatusOverride,
      setEmploymentStatusOverride,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPersona, employmentStatusOverride]
  )

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation(): ImpersonationContextValue {
  const context = useContext(ImpersonationContext)
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
